/**
 * Lista de comités (tenants) — Console del operador SaaS.
 *
 * Solo accesible para usuarios con `is_superadmin=true`. Ver SuperadminGate.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  CheckCircle2,
  Eye,
  Pause,
  Play,
  Plus,
  XCircle,
} from "lucide-react";

import { useToast } from "../../components/Toast";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  PageHeader,
  TableWrap,
  cn,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import { CrearEmpresaModal } from "./CrearEmpresaModal";
import {
  listarEmpresas,
  reactivarEmpresa,
  suspenderEmpresa,
  type EmpresaConMetricas,
} from "./api";

export function EmpresasListPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<"todas" | "activas" | "suspendidas">("todas");
  const [crearOpen, setCrearOpen] = useState(false);

  const empresas = useQuery({
    queryKey: ["superadmin", "empresas"],
    queryFn: listarEmpresas,
  });

  const suspendMut = useMutation({
    mutationFn: suspenderEmpresa,
    onSuccess: () => {
      toast.success("Comité suspendido");
      queryClient.invalidateQueries({ queryKey: ["superadmin", "empresas"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo suspender"),
  });

  const reactivateMut = useMutation({
    mutationFn: reactivarEmpresa,
    onSuccess: () => {
      toast.success("Comité reactivado");
      queryClient.invalidateQueries({ queryKey: ["superadmin", "empresas"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo reactivar"),
  });

  const items = (empresas.data ?? []).filter((m) => {
    if (filter === "activas") return m.empresa.is_active;
    if (filter === "suspendidas") return !m.empresa.is_active;
    return true;
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Comités"
        icon={Building2}
        subtitle="Gestión de tenants del SaaS. Cada comité es una empresa jurídicamente independiente con sus propios datos aislados."
        actions={
          <Button icon={Plus} onClick={() => setCrearOpen(true)}>
            Nuevo comité
          </Button>
        }
      />

      {crearOpen && <CrearEmpresaModal onClose={() => setCrearOpen(false)} />}

      <div className="flex items-center gap-2 text-sm">
        <span className="text-neutral-500">Filtro:</span>
        {(["todas", "activas", "suspendidas"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={cn(
              "rounded-md px-3 py-1",
              filter === f
                ? "bg-brand-700 text-white"
                : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200",
            )}
          >
            {f[0]!.toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <TableWrap label="Comités">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase text-neutral-500">
            <tr>
              <th scope="col" className="px-3 py-2">Comité</th>
              <th scope="col" className="px-3 py-2">RUT</th>
              <th scope="col" className="px-3 py-2 text-right">Socios</th>
              <th scope="col" className="px-3 py-2 text-right">Contratos</th>
              <th scope="col" className="px-3 py-2 text-right">Lecturas mes</th>
              <th scope="col" className="px-3 py-2 text-right">Cierres pend.</th>
              <th scope="col" className="px-3 py-2">Última lectura</th>
              <th scope="col" className="px-3 py-2">Estado</th>
              <th scope="col" className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {empresas.isLoading && (
              <tr>
                <td className="px-3" colSpan={9}>
                  <Cargando />
                </td>
              </tr>
            )}
            {empresas.isError && (
              <tr>
                <td className="px-3 py-4" colSpan={9}>
                  <ErrorCarga error={empresas.error} onReintentar={() => empresas.refetch()} />
                </td>
              </tr>
            )}
            {!empresas.isLoading && items.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-neutral-500" colSpan={9}>
                  Sin resultados.
                </td>
              </tr>
            )}
            {items.map((m) => (
              <Row
                key={m.empresa.id}
                m={m}
                onSuspend={() => suspendMut.mutate(m.empresa.id)}
                onReactivate={() => reactivateMut.mutate(m.empresa.id)}
                disabled={suspendMut.isPending || reactivateMut.isPending}
              />
            ))}
          </tbody>
        </table>
      </TableWrap>
    </div>
  );
}

function Row({
  m,
  onSuspend,
  onReactivate,
  disabled,
}: {
  m: EmpresaConMetricas;
  onSuspend: () => void;
  onReactivate: () => void;
  disabled: boolean;
}): JSX.Element {
  const ult = m.ultima_lectura_at
    ? new Date(m.ultima_lectura_at).toLocaleDateString("es-CL")
    : "—";
  return (
    <tr className="border-t border-neutral-100 hover:bg-neutral-50">
      <td className="px-3 py-2">
        <div className="font-medium text-neutral-900">{m.empresa.nombre}</div>
        <div className="text-xs text-neutral-500">{m.empresa.email}</div>
      </td>
      <td className="px-3 py-2 text-neutral-600">{m.empresa.rut}</td>
      <td className="px-3 py-2 text-right tabular-nums">{m.total_socios_activos}</td>
      <td className="px-3 py-2 text-right tabular-nums">{m.total_contratos_vigentes}</td>
      <td className="px-3 py-2 text-right tabular-nums">{m.lecturas_mes_actual}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {m.cierres_pendientes > 0 ? (
          <Badge tone="amber">{m.cierres_pendientes}</Badge>
        ) : (
          0
        )}
      </td>
      <td className="px-3 py-2 text-neutral-600 whitespace-nowrap">{ult}</td>
      <td className="px-3 py-2">
        {m.empresa.is_active ? (
          <Badge tone="emerald">
            <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} /> Activa
          </Badge>
        ) : (
          <Badge tone="rose">
            <XCircle className="h-3 w-3" strokeWidth={1.5} /> Suspendida
          </Badge>
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="flex items-center gap-2 justify-end">
          <Link
            to={`/superadmin/empresas/${m.empresa.id}`}
            className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
            title="Ver detalle"
          >
            <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
            Ver
          </Link>
          {m.empresa.is_active ? (
            <button
              type="button"
              onClick={onSuspend}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
              title="Suspender comité"
            >
              <Pause className="h-3.5 w-3.5" strokeWidth={1.5} />
              Suspender
            </button>
          ) : (
            <button
              type="button"
              onClick={onReactivate}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
              title="Reactivar comité"
            >
              <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
              Reactivar
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
